import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';

import { AutoCompleteSelectUI, ButtonUI, DatePickerUI, FormErrorUI, FormLabelUI, TimePickerUi } from '../Interface';
import dayjs from 'dayjs';
import DailyReportIcons from './DailyReportIcons';
import { Typography } from '@mui/material';
import useParam from '../../context/ParamContext';
import { generateAutocompleteItems } from '../../utils/helper';

const FormFields = ({ autocompleteOptions, viewField, autoCompleteData }) => {
  const { params } = useParam();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  useEffect(() => {
    if (values.date && values.startTime) {
      const combinedStart = dayjs(values.date)
        .hour(dayjs(values.startTime).hour())
        .minute(dayjs(values.startTime).minute())
        .second(0)
        .millisecond(0)
        .toISOString();
      setFieldValue('startDate', combinedStart);
    }
  }, [values.date, values.startTime, setFieldValue]);

  useEffect(() => {
    if (values.date && values.endTime) {
      const combinedEnd = dayjs(values.date)
        .hour(dayjs(values.endTime).hour())
        .minute(dayjs(values.endTime).minute())
        .second(0)
        .millisecond(0)
        .toISOString();
      setFieldValue('endDate', combinedEnd);
    }
  }, [values.date, values.endTime, setFieldValue]);

  const handleAddUsers = (selectedBrigade) => {
    const filteredR1 = autoCompleteData.resourceOne.filter((item) => item.statusId === selectedBrigade && item.active);

    let prioritized = [...filteredR1];

    if (params.DAILYREPORT_USER_ORDER_BY_GROUP) {
      const priorityGroupId = parseInt(params.DAILYREPORT_USER_ORDER_BY_GROUP, 10);

      if (!isNaN(priorityGroupId)) {
        prioritized = prioritized.sort((a, b) => {
          const pa = Number(a.groupId) === priorityGroupId ? 0 : 1;
          const pb = Number(b.groupId) === priorityGroupId ? 0 : 1;
          return pa - pb;
        });
      }
    }

    const generatedDataset = generateAutocompleteItems(prioritized, 'name', 'id');

    setFieldValue('dailyReportUser', generatedDataset ?? []);
  };

  return (
    <div className="grid grid-cols-4 gap-x-4 gap-y-4 mx-4 mb-5">
      <div className="col-span-4">
        <Field
          type="autocomplete"
          name="projectId"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Projekt" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.projectId} />}
          onChange={(_e, newVal) => {
            setFieldValue('projectId', newVal?.value ?? null).then(() => {
              validateField('projectId');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.projectId !== '' }}
          selectedValue={values.projectId}
          selectedLabelValue={values?.project?.projectName}
          dataset={autocompleteOptions.project}
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          type="date"
          name="date"
          component={DatePickerUI}
          value={values.date}
          fullWidth
          label={<FormLabelUI text="Dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.date} />}
          onChange={(date) => {
            setFieldValue('date', date).then(() => {
              validateField('date');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.date !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="time"
          name="startTime"
          component={TimePickerUi}
          value={values.startTime}
          fullWidth
          label={<FormLabelUI text="Kezdés" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.startTime} />}
          onChange={(date) => {
            setFieldValue('startTime', date).then(() => {
              validateField('startTime');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.startTime !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="time"
          name="endTime"
          component={TimePickerUi}
          value={values.endTime}
          fullWidth
          label={<FormLabelUI text="Befejezés" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.endTime} />}
          onChange={(date) => {
            setFieldValue('endTime', date).then(() => {
              validateField('endTime');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.endTime !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        {params.DAILYREPORT_BRIGADE_BUTTON === 'Y' && (
          <AutoCompleteSelectUI
            label={<FormLabelUI text="Brigádok" />}
            dataset={autocompleteOptions.brigades}
            selectedValue={values.brigade}
            onChange={(_e, newVal, _reason) => {
              handleAddUsers(newVal?.value);
              setFieldValue('brigade', newVal?.value ?? null);
            }}
            disabled={viewField}
          />
        )}
      </div>
      <div
        className={`${params.DAILYREPORT_BRIGADE_BUTTON !== 'Y' ? 'md:col-span-4' : 'md:col-span-3'} col-span-4 flex items-center gap-2`}
      >
        <Field
          type="autocomplete"
          name="dailyReportUser"
          component={AutoCompleteSelectUI}
          fullWidth
          label={<FormLabelUI text="Dolgozók" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.dailyReportUser} />}
          onChange={(_e, newVal) => {
            setFieldValue('dailyReportUser', newVal ?? []).then(() => {
              validateField('dailyReportUser');
            });
          }}
          InputLabelProps={{ shrink: values.dailyReportUser?.length }}
          selectedValue={values.dailyReportUser ?? []}
          freeSolo
          multiple
          disabled={viewField}
          getOptionLabel={(option) => option.label}
          dataset={autocompleteOptions.resourceOne}
        />
        <Typography mt="1rem">{values.dailyReportUser?.length}</Typography>
      </div>
      <div className="col-span-4">
        <DailyReportIcons disabled={viewField} />
      </div>
    </div>
  );
};

export default FormFields;
